import { useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";

import axios from "axios";
import { t } from "i18next";

import {
  ShareTransmission, 
  LiveContainer, 
  LiveInfos, 
  ChatContainer, 
  Dot, 
  DateAndHour, 
  Row, 
  Column, 
  DisplayMore, 
  StyledContainer,
  MaterialsContainer,
} from './style';
import { StyledTypography } from "../Talk/style";
import { ITalksProps } from "../../../interfaces/university/talk";
import { ChannelContext, IGroup } from "../../../context/channel";
import { SmallThumb } from "../../../components/whitelabel/samllThumb/SmallThumb";
import AttachmentIcon from "../../../components/shared/Icons/AttachmentIcon";
import { EMBED_URL, UPLOAD_URL } from "../../../settings";
import TopMenu from "../../../components/whitelabel/topMenu/TopMenu";
import Button from "../../../components/shared/button/Button";
import { ArrowUp } from "../../../components/shared/Icons/ArrowUp";
import { TalkDescription } from "../../../components/whitelabel/talkDescription/TalkDescription";
import { IconShare } from "../../../components/shared/Icons/IconShare";
import Footer from "../../../components/whitelabel/footer/Footer";
import colors from "../../../theme/colors";
import { ClapprPlayer, TypePlayerView } from 'evts-player';
import { breakpointSize } from "../../../theme/mediaQuerie";

type TypeLiveProps = {
  talk: ITalksProps;
  group?: IGroup;
  email: string;
  userSubscribedKey?: string;
}

interface IMoreTalks {
  upcoming: ITalksProps[];
  live: ITalksProps[];
  recorded: ITalksProps[];
}

interface IMaterial {
  url: string;
  name: string;
}

// return a list of talks
const MoreTalksList = (talks: ITalksProps[], title: string, color: string, channel: string, history: any) => {
  return (
    <>
      {talks.length > 0 && <StyledTypography type="subheading3" color="placeholder">{t(title).toUpperCase()}</StyledTypography>}
      {talks.map(talk => (
        <SmallThumb
          onClick={() => history.push(`/${channel}/${talk.slug}`)}
          key={talk.slug}
          object={{ date: talk.date, title: talk.title, cover: talk.cover }}
          color={color}
        />
      ))}
    </>
  );
};

// return a list of pdf's to download
const MaterialsList = (materials: IMaterial[], color: string) => {
  return (
    <>
      <StyledTypography style={{ width: "100%" }} type="subheading3" color="label">
        {t("talk.materials").toUpperCase()}
      </StyledTypography>
      {materials.map(material => (
        <a target="_blank" href={material.url} rel="noreferrer">
          <div className="icon-container"><AttachmentIcon /></div>
          <StyledTypography type="body4" customColor={color} className="break-text">
            {material.name || material.url}
          </StyledTypography>
          <StyledTypography type="body4" color="title">DOWNLOAD</StyledTypography>
        </a>
      ))}
    </>
  );
};

const isMobile = window.innerWidth <= breakpointSize.Sm;

export const Live = ({talk, group, email}: TypeLiveProps) => {
  const { channel, data} = useContext(ChannelContext);

  const history = useHistory();

  const [searchString, setSearchString] = useState<string>("");
  const [chatUrl, setChatUrl] = useState<string>("");
  const [displayChat, setDisplayChat] = useState<boolean>(true);
  const [displayChatMobile, setDisplayChatMobile] = useState<boolean>(true);
  const [moreTalks, setMoreTalks] = useState<IMoreTalks>({
    upcoming: [],
    live: [],
    recorded: []
  });
  const [materials, setMaterials] = useState<IMaterial[]>([])
  const [playerView, setPlayerView] = useState<TypePlayerView>(talk.state);

  /* const [modalDisconnected, setModalDisconnected] = useState(false); */

const getMaterials = useCallback(async () => {
  try {
    const response = await axios.get(UPLOAD_URL + talk.owner_id + '/' + talk.id);
    if(response.status === 200){
      setMaterials(response.data);
    }
  } catch(error: any) {
    if(error.message && error.message.includes("404")){
      console.info("no material found for this webinar");
    }
    else {
      console.error(error);
    }
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [talk.id]);

useEffect(() => {
  window.scrollTo({
    top: 0,
    behavior: "smooth"
  });
}, []);

useEffect(() => {
  const urlChat = `${EMBED_URL}chat-embed/${talk.playback_stream_name}/?username=${(email && email.split('@')[0]) || ''}&email=${email || ''}`;
  setChatUrl(urlChat);
}, [talk, email]);

useEffect(() => {
  let moreTalks: IMoreTalks = {
    upcoming: [],
    live: [],
    recorded: []
  };
  const slug = talk.slug;
  group && group.talks.forEach(talk => {
    // Remove current webinar to not display duplicated talks
    if (talk.slug !== slug && talk.state !== "processing") {
      moreTalks[talk.state].push(talk);
    }
  });

  if (talk.state === "recorded") {
    setDisplayChat(false);
  }
  setMoreTalks(moreTalks);
  getMaterials();

}, [getMaterials, group, talk]);

  return (
    <StyledContainer >
      <Helmet>
        <title>{`Webinar - ${talk?.title}`}</title>
        <meta property="og:title" content={`Webinars - ${talk?.title}`} />
        <meta property="og:description" content={talk?.description}/>  
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={`Webinars - ${talk?.title}`} />
        <meta property="og:image" content={data.images.logo} />
        <meta property="og:image:alt" content={talk?.description} />
      </Helmet>

      <TopMenu
        trilhaConteudo
        transparent={false}
        logoLightMode={data.images.logo}
        searchString={searchString}
        setSearchString={setSearchString}
      />

      {/* { modalDisconnected && 
        <ModalDisconnected onCancel={() => {
          if(userSubscribedKey) {
            localStorage.removeItem(userSubscribedKey);
          }
          setModalDisconnected(false);
          history.replace("/" + channel);
        }} setToggleModal={setModalDisconnected} toggleModal={modalDisconnected} />
      } */}

      <LiveContainer>
        <ClapprPlayer
          /* setModalDisconnected={setModalDisconnected} */
          channel={channel ?? ""}
          playlist={(group && window.location.href.includes(`?playlist=${group?.slug}`)) ? group : undefined} 
          email={email} 
          talk={talk}
          playerView={playerView}
          setPlayerView={setPlayerView}
        />
        <ChatContainer className={displayChatMobile ? "" : "display-mobile-container"}>
          <div className="buttons-container">
            {
              (talk.state !== "recorded") && 
            <Button
              onClick={() => setDisplayChat(true)}
              size={isMobile ? "small" : "medium"}
              enable
              typographyType='subheading3'
                  style={{borderRadius: '30px'}}
                  customBackground = {data.config.colors?.colorPrimary}
              > CHAT </Button>
            }
            <Button
              onClick={() => setDisplayChat(false)}
              size={isMobile ? "small" : "medium"}
              enable
              typographyType='subheading3'
              style={{borderRadius: '30px'}}
              variant="outlined"
              customColor={data.config.colors?.colorPrimary}
              customBackground={data.config.colors?.colorPrimary}
            > {t('seeMore').toUpperCase()} </Button>
            <ArrowUp
              className={`transition ${displayChatMobile ? "animate-up" : "animate-down"} down-up-button`}
              color={data.config.colors.colorPrimary} 
                onClick={() => setDisplayChatMobile(!displayChatMobile)} 
            />
          </div>
          {
            displayChat ? 
            (
              // embed chat
              <iframe  
              title={chatUrl}
              className={displayChatMobile ? "display" : "hidden"}
              style={{borderRadius: '8px'}}
              src={chatUrl}
              // webkitAllowFullScreen
              // mozallowfullscreen
              allowFullScreen
              frame-border="0"
              scrolling="no"
              margin-height={0}
                margin-width={0}
                />
                ) : (
              // list other talks of group
              <DisplayMore className={displayChatMobile ? "display" : "hidden"}>
                { MoreTalksList(moreTalks.upcoming, 'next', data.config.colors.colorPrimary, channel!, history) }
                { MoreTalksList(moreTalks.live, 'liveNow', data.config.colors.colorPrimary, channel!, history) }
                { MoreTalksList(moreTalks.recorded, 'previous', data.config.colors.colorPrimary, channel!, history) }
              </DisplayMore>
            )
         }
        </ChatContainer>
      </LiveContainer>
      
      <LiveInfos className={displayChatMobile ? "hidden" : "display"}>
        <StyledTypography style={{margin: "5px"}} className="title" color="title" type="heading4">{talk.title}</StyledTypography>
        <div>
          { (!talk.hide_recorded && 
              ["processing", "recorded", "live", "upcoming"].includes(playerView)) && 
                <Button
                  style={{padding: '4px 8px', borderRadius: '3px'}} 
                  variant="contained" 
                  size="small"
                  typographyType={isMobile ? "subheading3" : "subheading2"}
                  colorBackground={playerView === "processing" ? "placeholder" : "primary" }
                  customBackground={playerView === "recorded" ? data.config.colors.colorPrimary: undefined}
                > 
                  { t(`talk.state.${playerView}`).toUpperCase() }
                </Button>
          }
          <DateAndHour>
            {talk.date.split("às")[0] + " " + t('date.at') + " " + talk.date.split("às")[1]}
          </DateAndHour>
          {
            group && 
            <Row>
              <StyledTypography color='label' type='body2'>{t('group')}</StyledTypography>
              <Dot />
              <StyledTypography color='label' type='body2'>{group.name}</StyledTypography> 
            </Row>
          }
        </div>

        <TalkDescription object={talk} />

        <MaterialsContainer>
          { talk.slides &&     
            <>
              <StyledTypography style={{width: '100%'}} type="subheading3" color="label">{t('talk.slides').toUpperCase()}</StyledTypography> 
              <a target="_blank" href={talk.slides} rel="noreferrer">
                <div className="icon-container"><AttachmentIcon/></div>
                <StyledTypography type="body4" customColor={data.config.colors.colorPrimary} className="break-text">{talk.slides}</StyledTypography>
                <StyledTypography type="body4" color="title">DOWNLOAD</StyledTypography>
              </a>
              </>
          }
          { MaterialsList(materials, data.config.colors.colorPrimary) }
        </MaterialsContainer>
      </LiveInfos>

      {!talk.hide_share_links && (
        <ShareTransmission className={displayChatMobile ? "hidden" : "display"}>
          <Column>
            <StyledTypography style={{margin: '0 auto'}} type="subheading3" color="body">{t('talk.share')}</StyledTypography>
            <div className="share-icons-container">
                <IconShare
                  url={window.location.href}
                  type={"facebook"}
                  color={data.config.colors.colorPrimary}
                />
                <IconShare
                  url={window.location.href}
                  type={"twitter"}
                  color={data.config.colors.colorPrimary}
                />
                <IconShare
                  url={window.location.href}
                  type={"linkedin"}
                  color={data.config.colors.colorPrimary}
                />
              <IconShare
                url={window.location.href}
                type={"url"}
                color={data.config.colors.colorPrimary}
              />
            </div>
          </Column>

          <StyledTypography type="subheading3" color="body">
            {t("talk.TroubleWatching")}
            <a href="https://eventials.movidesk.com/kb/form/3385/" rel="noreferrer" target="_blank">
              <StyledTypography type="subheading3" customColor={data.config.colors.colorPrimary}> {t("talk.TroubleWatching.talkToUs")}</StyledTypography>
            </a>
          </StyledTypography>

        </ShareTransmission>
      )}
      <Footer
        className={displayChatMobile ? "hidden" : "display-footer"}
        companyName={data.name}
        image={data.footer.logo ? data.footer.logo : data.images.logo}
        color={data ? data.footer.color : colors.black}
        textColor={data ? data.footer.footerTextColor : colors.black}
        social={data.footer.social}
      />
    </StyledContainer>
  );
}